import { useEffect, useMemo, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useSnapshot } from 'valtio'

import { GetTenantConfigDocument } from '@app/graphql'
import { TenantConfigState } from '@app/storage'


export class TenantValueResolverError extends Error {
  readonly path: string
  readonly resolvedPath: string

  constructor(
    message: string,
    path: string,
    resolvedPath: string
  ) {
    super(message)

    this.name = 'TenantValueResolverError'
    this.path = path
    this.resolvedPath = resolvedPath

    Object.setPrototypeOf(this, new.target.prototype)
  }
}

function isObject(
  value: unknown
): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value)
  )
}

function isTraversable(
  value: unknown
): value is object {
  return value !== null && typeof value === 'object'
}

function describeValue(value: unknown): string {
  if (value === null) {
    return 'null'
  }

  if (Array.isArray(value)) {
    return 'array'
  }

  return typeof value
}

function resolveArrayItem(
  array: readonly unknown[],
  lookup: string,
  path: string,
  resolvedPath: string
): unknown {
  const matches: unknown[] = []

  for (const item of array) {
    if (!isObject(item)) {
      continue
    }

    for (const value of Object.values(item)) {
      if (value === lookup) {
        matches.push(item)
        break
      }
    }
  }

  if (matches.length === 0) {
    throw new TenantValueResolverError(
      `No array element matching '${lookup}' was found at '${resolvedPath}'.`,
      path,
      resolvedPath
    )
  }

  if (matches.length > 1) {
    throw new TenantValueResolverError(
      `Ambiguous array lookup '${lookup}' at '${resolvedPath}': ` +
        `${matches.length} elements matched.`,
      path,
      resolvedPath
    )
  }

  return matches[0]
}

export function createTenantValueResolver<T extends object>(
  config: T
) {
  return function tenantValue(path: string): unknown {
    if (typeof path !== 'string' || path.trim() === '') {
      throw new TenantValueResolverError(
        'Path must be a non-empty string.',
        path,
        ''
      )
    }

    const parts = path.split('.')

    if (parts.some(part => part.length === 0)) {
      throw new TenantValueResolverError(
        `Invalid path '${path}': empty path segments are not allowed.`,
        path,
        ''
      )
    }

    let current: unknown = config
    const resolvedParts: string[] = []

    for (let index = 0; index < parts.length; index++) {
      const part = parts[index]
      const isLast = index === parts.length - 1
      const resolvedPath = resolvedParts.join('.')

      if (
        current === null ||
        typeof current !== 'object'
      ) {
        throw new TenantValueResolverError(
          `Cannot traverse '${path}': ` +
            `'${resolvedPath}' resolves to ` +
            `${describeValue(current)}, but the path continues with '${part}'.`,
          path,
          resolvedPath
        )
      }
      if (Array.isArray(current)) {
        current = resolveArrayItem(
          current,
          part,
          path,
          resolvedPath
        )

        resolvedParts.push(part)

        continue
      }

      if (!isObject(current)) {
        throw new TenantValueResolverError(
          `Cannot traverse '${path}' at '${resolvedPath}'.`,
          path,
          resolvedPath
        )
      }

      if (!Object.prototype.hasOwnProperty.call(current, part)) {
        throw new TenantValueResolverError(
          `Property '${part}' does not exist at ` +
            `'${resolvedPath || '<root>'}' while resolving '${path}'.`,
          path,
          resolvedPath
        )
      }

      current = current[part]
      resolvedParts.push(part)

      if (isLast) {
        return current
      }

      if (!isTraversable(current)) {
        throw new TenantValueResolverError(
          `Cannot continue resolving '${path}': ` +
            `'${resolvedParts.join('.')}' resolves to ` +
            `${describeValue(current)}, but the path continues.`,
          path,
          resolvedParts.join('.')
        )
      }
    }

    return current
  }
}

const createTenantValueGetter = (
  resolver: (path: string) => unknown,
  ready: boolean,
) => {
  return (path: string, defaultValue=undefined) => {
    if (!ready) {
      return defaultValue
    }

    try {
      const value = resolver(path)

      return value === undefined
        ? defaultValue
        : value
    } catch (err) {
      console.warn(err)
      return defaultValue
    }
  }
}

export const useTenantConfig = () => {
  const [ tenantConfigReady, setTenantConfigReady ] = useState(false)
  const tenantConfig = useSnapshot(TenantConfigState)

  const resolver = useMemo(
    () => createTenantValueResolver(tenantConfig),
    [tenantConfig],
  )
  const getTenantValue = useMemo(
    () => createTenantValueGetter(resolver, tenantConfigReady),
    [resolver, tenantConfigReady],
  )

  const [
    GetTenantConfig,
    { loading, error: tenantConfigError, data },
  ] = useLazyQuery(GetTenantConfigDocument, {
    fetchPolicy: 'cache-first'
  })

  useEffect(() => {
    GetTenantConfig()
  }, [GetTenantConfig])

  useEffect(() => {
    if (tenantConfigError) {
      setTenantConfigReady(true)
    }
    if (data?.tenantConfig) {
      Object.assign(TenantConfigState, data?.tenantConfig)
      setTenantConfigReady(true)
    }
  }, [tenantConfigError, data])

  return {
    getTenantValue,
    tenantConfig,
    tenantConfigLoading: !tenantConfigReady || loading,
    tenantConfigError,
  }
}
